import React, { useState, useEffect } from 'react';
import { useBooking, CustomerInfo } from '../../contexts/booking/BookingContext';
import { supabase } from '../../lib/supabaseClient';

const CustomerInfoForm: React.FC = () => {
  const { bookingState, setCustomerInfo, nextStep, prevStep } = useBooking();
  const { customerInfo } = bookingState;
  
  const [formData, setFormData] = useState<CustomerInfo>({
    firstName: customerInfo?.firstName || '',
    lastName: customerInfo?.lastName || '',
    email: customerInfo?.email || '',
    phone: customerInfo?.phone || '',
    notes: customerInfo?.notes || ''
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof CustomerInfo, string>>>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is logged in and fetch profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);

        // Check if user is authenticated
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          setIsLoggedIn(true);

          // Fetch user profile
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (error) {
            console.error('Error fetching user profile:', error);
            // We don't need to create a profile here - that will happen during booking
          } else if (profile) {
            // Pre-fill form with profile data
            const nameParts = profile.full_name ? profile.full_name.split(' ') : ['', ''];
            const firstName = profile.preferred_name || nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            setFormData({
              firstName,
              lastName,
              email: profile.email || user.email || '',
              phone: profile.phone_number || '',
              notes: ''
            });
          }
        } else {
          // User is not logged in - leave form empty for manual entry
          setIsLoggedIn(false);
        }
      } catch (err) {
        console.error('Error checking authentication:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof CustomerInfo]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CustomerInfo, string>> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setCustomerInfo(formData);
      nextStep();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2D8C5]"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Your Information</h2>
      <p className="text-gray-600 mb-6">Please provide your contact details for your appointment</p>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-[#232347] font-medium mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.firstName 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>
          
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-[#232347] font-medium mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.lastName 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-[#232347] font-medium mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.email 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-[#232347] font-medium mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.phone 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>
        </div>
        
        {/* Notes/Special Requests */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-[#232347] font-medium mb-1">
            Special Requests or Notes (optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]/50"
            placeholder="Any special requests, health concerns, or preferences we should know about..."
          />
        </div>
        
        {/* Account creation prompt for non-logged in users */}
        {!isLoggedIn && (
          <div className="mb-6 bg-[#F1F7F4] p-4 rounded-md">
            <h3 className="text-lg font-medium text-[#232347] mb-2">Create an Account?</h3>
            <p className="text-gray-600 mb-3">
              Create an account to easily manage your appointments and get special offers!
            </p>
            <a 
              href="/signup" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#B2D8C5] font-medium hover:underline"
            >
              Sign up now
            </a>
          </div>
        )}
        
        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerInfoForm;
